import {
  TargetSchema,
  type ClaimantProjection,
  type ClaimItemRowForecast,
  type ExtractedRecord,
  type FieldMapping,
} from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { getRecordData } from "./recordUtils";
import { parseMoneyCell } from "./amounts";

/**
 * "This will" rows for the approve confirm (#7440 AC2), built from the same
 * projection, exclusions and loaded records the grid shows, so the dialog can
 * never promise something the screen did not.
 *
 * "Won't count toward deductible" (#7436 AC5) lists the claim lines the
 * projection judged outside the policy's paid or incurred window; the Layout
 * line reads "Automatic extraction" until saved layouts ship (#7438).
 */
export interface ApproveSummaryRow {
  key: string;
  label: string;
  value: string;
}

export interface ApproveSummary {
  rows: ApproveSummaryRow[];
  /** Claimant rows the counts were computed over (loaded, not excluded). */
  loadedClaimants: number;
  /** Whole-file claimant count when known and different from the loaded count (AC5). */
  totalClaimants: number | null;
  /** True when the counts cover a loaded window rather than the whole file. */
  partial: boolean;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
const int = (n: number) => n.toLocaleString();

const nameOf = (claimant: ExtractedRecord, projection: ClaimantProjection | undefined): string => {
  if (projection?.claimant_name) return projection.claimant_name;
  const d = getRecordData(claimant, TargetSchema.CLAIMANT);
  const full = d.claimant_name ?? [d.claimant_first_name, d.claimant_last_name].filter(Boolean).join(" ");
  return full ? String(full) : "Unnamed claimant";
};

/** "2 · Alex Rivera, Sam Okafor" — names capped so a 400-claimant file does not become a paragraph. */
const MAX_NAMES = 4;
const countWithNames = (names: string[]): string => {
  if (names.length === 0) return "None";
  const shown = names.slice(0, MAX_NAMES);
  const more = names.length - shown.length;
  return `${int(names.length)} · ${shown.join(", ")}${more > 0 ? ` and ${int(more)} more` : ""}`;
};

export const buildApproveSummary = ({
  claimants,
  linesOf,
  isExcluded,
  projectionFor,
  partial,
  totalClaimants,
  rowForecastFor,
  isClaimantExcluded,
  engine,
}: {
  /** Loaded claimant-schema records. */
  claimants: ExtractedRecord[];
  /**
   * Whether a CLAIMANT is excluded. In a row-per-line file the claimant's top
   * row is also its first claim line, so `isExcluded(claimant)` answers for
   * that one line; the caller passes the claimant rule (every line excluded)
   * here. Defaults to `isExcluded`.
   */
  isClaimantExcluded?: (claimant: ExtractedRecord) => boolean;
  /** Per-line forecast (#7436): a line with counts_toward_deductible=false loads but does not count. */
  rowForecastFor?: (line: ExtractedRecord) => ClaimItemRowForecast | undefined;
  /**
   * `policy_windows.engine`. Under `legacy` the flagged lines load stamped
   * ineligible but the load still sums them, so the row is labelled for what
   * it is rather than "won't count".
   */
  engine?: "deductible_basis" | "legacy" | null;
  linesOf: (claimant: ExtractedRecord) => ExtractedRecord[];
  isExcluded: (record: ExtractedRecord) => boolean;
  projectionFor: (claimant: ExtractedRecord) => ClaimantProjection | undefined;
  /** Loaded set is a window of the file (more pages / windowed projection). */
  partial: boolean;
  /** Whole-file claimant count from the server summary, when known. */
  totalClaimants?: number | null;
}): ApproveSummary => {
  let liveLines = 0;
  let paidSum = 0;
  let wontCountLines = 0;
  let wontCountSum = 0;
  let excludedLines = 0;
  let excludedClaimants = 0;
  let updateYtd = 0;
  const created: string[] = [];
  const stopLoss: string[] = [];
  let liveClaimants = 0;

  const isClaimLine = (r: ExtractedRecord) =>
    (r.extracted_fields ?? []).some((f) => f.target_schema === TargetSchema.CLAIM_ITEM);

  const claimantExcluded = isClaimantExcluded ?? isExcluded;
  for (const claimant of claimants) {
    const lines = linesOf(claimant).filter(isClaimLine);
    if (claimantExcluded(claimant)) {
      excludedClaimants += 1;
      // Lines go with their claimant (exclusion cascade) — counted as skipped once, here.
      excludedLines += lines.length;
      continue;
    }
    liveClaimants += 1;
    for (const line of lines) {
      if (isExcluded(line)) {
        excludedLines += 1;
        continue;
      }
      liveLines += 1;
      const paid = parseMoneyCell(getRecordData(line, TargetSchema.CLAIM_ITEM).paid_amount);
      if (paid !== null) paidSum += paid;
      if (rowForecastFor?.(line)?.counts_toward_deductible === false) {
        wontCountLines += 1;
        if (paid !== null) wontCountSum += paid;
      }
    }
    const proj = projectionFor(claimant);
    if (!proj) continue;
    if (proj.match_status === "new") created.push(nameOf(claimant, proj));
    else updateYtd += 1;
    if (proj.will_create_sl_claim) stopLoss.push(nameOf(claimant, proj));
  }

  const skipParts: string[] = [];
  if (excludedClaimants > 0)
    skipParts.push(`${int(excludedClaimants)} ${plural(excludedClaimants, "claimant", "claimants")} you excluded`);
  if (excludedLines > 0)
    skipParts.push(`${int(excludedLines)} claim ${plural(excludedLines, "line", "lines")} you excluded`);

  const rows: ApproveSummaryRow[] = [
    { key: "lines", label: "Add claim lines", value: `${int(liveLines)} · ${formatUSD(paidSum)} paid` },
    ...(wontCountLines > 0
      ? [
          {
            key: "wont_count",
            label: engine === "legacy" ? "Loads stamped ineligible" : "Won't count toward deductible",
            value: `${int(wontCountLines)} claim ${plural(wontCountLines, "line", "lines")} · ${formatUSD(wontCountSum)}`,
          },
        ]
      : []),
    {
      key: "ytd",
      label: "Update claimant YTD",
      value: `${int(updateYtd)} ${plural(updateYtd, "claimant", "claimants")}`,
    },
    { key: "create", label: "Create claimants", value: countWithNames(created) },
    { key: "slc", label: "Create stop-loss claims", value: countWithNames(stopLoss) },
    { key: "skip", label: "Skip", value: skipParts.length ? skipParts.join(" · ") : "Nothing" },
  ];

  const total = typeof totalClaimants === "number" && totalClaimants > claimants.length ? totalClaimants : null;
  return { rows, loadedClaimants: liveClaimants, totalClaimants: total, partial: partial || total !== null };
};

/** One "Money and date columns" line: which source header feeds the field (#7440 AC3). */
export interface MappingRecapRow {
  key: string;
  field: string;
  /** Source header the mapping reads, or null when the field is not mapped. */
  source: string | null;
}

// Target field names in order of preference. The claims templates in use map
// the service date to `date_of_service_start` (the local Anthem template,
// 2026-10-15); `service_start_date` is the reimbursement-side name and
// `date_of_service` the seed-fixture one.
const RECAP_FIELDS: Array<{ key: string; field: string; schema: TargetSchema; targets: string[] }> = [
  { key: "paid", field: "Paid amount", schema: TargetSchema.CLAIM_ITEM, targets: ["paid_amount"] },
  {
    key: "service",
    field: "Service date",
    schema: TargetSchema.CLAIM_ITEM,
    targets: ["date_of_service_start", "date_of_service", "service_start_date"],
  },
  { key: "ytd", field: "Reported YTD", schema: TargetSchema.CLAIMANT, targets: ["ytd_total_paid"] },
];

/**
 * The three mappings a wrong column would silently corrupt money or dates
 * through. The column letter the design shows is not on the mapping (the
 * template records the detected header, not a sheet coordinate), so the row
 * is the header alone.
 */
export const buildMappingRecap = (fieldMappings: FieldMapping[] | undefined): MappingRecapRow[] =>
  RECAP_FIELDS.map(({ key, field, schema, targets }) => {
    const m = targets
      .map((target) => (fieldMappings ?? []).find((fm) => fm.target_schema === schema && fm.target_field === target))
      .find(Boolean);
    const source = m?.extraction_location?.key?.trim();
    return { key, field, source: source ? source : null };
  });
